import { useEffect, useState } from 'react';

/**
 * Track the current theme (value of `data-theme` on <html>).
 *
 * Components that render theme-dependent content outside of React's declarative
 * style pipeline (canvas charts, imperative SVG draws, Chart.js colors that are
 * captured at render time from CSS vars, etc.) must re-render when the theme
 * changes. Consuming this hook subscribes to a MutationObserver so any change
 * to `document.documentElement`'s `data-theme` attribute triggers a re-render.
 */
export const useTheme = () => {
  const [theme, setTheme] = useState(() => {
    if (typeof document === 'undefined') return 'light';
    return document.documentElement.getAttribute('data-theme') || 'light';
  });

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    const update = () => {
      const next = root.getAttribute('data-theme') || 'light';
      setTheme(prev => (prev === next ? prev : next));
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  return theme;
};
